import { useCallback, useMemo, useState } from 'react'

import { F0Box, F0Button, F0Dialog, F0Text } from '@factorialco/f0-react'
import { Spinner } from '@factorialco/f0-react/dist/experimental'
import {
  NewRequestPageDocument,
  ProcurementTypeItemFragment,
  ProcurementTypeVersionsDocument
} from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { VersionHistoryPanel, type VersionNode } from './VersionHistoryPanel'
import { VersionPreview } from './VersionPreview'

export interface RestoredVersionData {
  versionId: string
  formId: string
  workflowId: string
  versionNumber: number
  authorFirstName: string
  authorLastName: string
}

interface VersionHistoryProps {
  procurementTypeId: string
  companyId: string
  requestType: ProcurementTypeItemFragment | null
  onClose: () => void
  onRestore: (data: RestoredVersionData) => void
}

export function VersionHistory({
  procurementTypeId,
  companyId,
  requestType,
  onClose,
  onRestore
}: VersionHistoryProps) {
  const [selectedVersionId, setSelectedVersionId] = useState<string | 'current'>('current')

  const { data, loading, networkActivity } = useQuery(ProcurementTypeVersionsDocument, {
    variables: { procurementTypeId }
  })

  const { data: currencyData } = useQuery(NewRequestPageDocument, {
    variables: { companyId, enabled: true }
  })

  const currencies = useMemo(
    () => currencyData?.apiCore?.currenciesConnection?.edges?.map((e) => e.node) ?? [],
    [currencyData]
  )
  const defaultCurrency =
    currencyData?.apiCore?.currentsConnection?.nodes?.[0]?.employee?.legalEntity?.currency

  const allVersions: VersionNode[] = useMemo(
    () => data?.procurement?.typeVersionsConnection?.nodes ?? [],
    [data]
  )

  const activeVersion = useMemo(
    () => allVersions.find((v) => v.status === 'active') ?? null,
    [allVersions]
  )

  const draftVersions = useMemo(
    () => allVersions.filter((v) => v.status === 'draft'),
    [allVersions]
  )

  const selectedVersion = useMemo(() => {
    if (selectedVersionId === 'current') return activeVersion
    return allVersions.find((v) => String(v.id) === selectedVersionId) ?? null
  }, [selectedVersionId, activeVersion, allVersions])

  const canRestore = selectedVersion != null && selectedVersion.status !== 'draft'

  const handleRestore = () => {
    if (!selectedVersion) return
    const author = selectedVersion.publishedBy ?? selectedVersion.createdBy
    onRestore({
      versionId: selectedVersion.id,
      formId: selectedVersion.formId,
      workflowId: selectedVersion.workflowId,
      versionNumber: selectedVersion.versionNumber,
      authorFirstName: author.firstName,
      authorLastName: author.lastName
    })
    onClose()
  }

  const isLoading = loading || networkActivity

  const hideDialogHeader = useCallback((node: HTMLElement | null) => {
    if (!node) return
    let el: Element | null = node
    while (el) {
      const prev = el.previousElementSibling
      if (prev instanceof HTMLDivElement) {
        prev.style.display = 'none'
        return
      }
      const parent: Element | null = el.parentElement
      if (!parent || parent.getAttribute('role') === 'dialog') break
      el = parent
    }
  }, [])

  return (
    <F0Dialog isOpen onClose={onClose} position="fullscreen" disableContentPadding>
      <F0Box
        ref={hideDialogHeader}
        display="flex"
        flexDirection="row"
        height="full"
        width="full"
        overflow="hidden"
        data-version-history-dialog
      >
        <F0Box display="flex" flexDirection="column" grow minWidth="0" overflow="hidden">
          <F0Box display="flex" alignItems="center" paddingX="lg" paddingY="md" shrink={false}>
            <F0Text content={i18n.t('common.preview')} variant="label" as="span" />
          </F0Box>
          {isLoading ? (
            <F0Box display="flex" alignItems="center" justifyContent="center" grow>
              <Spinner />
            </F0Box>
          ) : selectedVersion ? (
            <VersionPreview
              key={`${selectedVersion.id}-${selectedVersion.formId}-${selectedVersion.workflowId}`}
              formId={selectedVersion.formId}
              workflowId={selectedVersion.workflowId}
              companyId={companyId}
              requestType={requestType}
              currencies={currencies}
              defaultCurrency={defaultCurrency}
            />
          ) : (
            <F0Box display="flex" alignItems="center" justifyContent="center" grow padding="md">
              {i18n.t('procurement.types.version_history.empty_state')}
            </F0Box>
          )}
        </F0Box>

        <F0Box
          display="flex"
          flexDirection="column"
          shrink={false}
          width="80"
          height="full"
          overflow="hidden"
          borderLeft="default"
          borderColor="secondary"
        >
          {isLoading ? (
            <F0Box display="flex" alignItems="center" justifyContent="center" grow>
              <Spinner />
            </F0Box>
          ) : (
            <>
              <VersionHistoryPanel
                versions={allVersions}
                activeVersion={activeVersion}
                draftVersions={draftVersions}
                selectedVersionId={selectedVersionId}
                onSelectVersion={setSelectedVersionId}
                onClose={onClose}
              />

              {canRestore && (
                <F0Box
                  display="flex"
                  alignItems="center"
                  justifyContent="end"
                  gap="sm"
                  padding="lg"
                  borderTop="default"
                  borderColor="secondary"
                  shrink={false}
                >
                  <F0Button variant="outline" onClick={onClose} label={i18n.t('common.cancel')} />
                  <F0Button
                    variant="default"
                    onClick={handleRestore}
                    label={i18n.t('procurement.types.version_history.restore_button')}
                  />
                </F0Box>
              )}
            </>
          )}
        </F0Box>
      </F0Box>
    </F0Dialog>
  )
}
